import { useEffect, useRef, useState, type RefObject } from 'react';
import { useMutationObserverTarget, useTimeout } from '@mantine/hooks';
import { getEnv } from '../../core';
import { toInt } from '../ScrollArea/utils';

function isParent(
  parentElement: HTMLElement | EventTarget | null,
  childElement: HTMLElement | null
) {
  if (!childElement || !parentElement) {
    return false;
  }

  let parent = childElement.parentNode;
  while (parent != null) {
    if (parent === parentElement) {
      return true;
    }
    parent = parent.parentNode;
  }
  return false;
}

interface UseFloatingIndicatorInput {
  target: HTMLElement | null | undefined;
  parent: HTMLElement | null | undefined;
  ref: RefObject<HTMLElement | null>;
  displayAfterTransitionEnd?: boolean;
  onTransitionStart?: () => void;
  onTransitionEnd?: () => void;
}

export function useFloatingIndicator({
  target,
  parent,
  ref,
  displayAfterTransitionEnd,
  onTransitionStart,
  onTransitionEnd,
}: UseFloatingIndicatorInput) {
  const transitionTimeout = useRef<number>(-1);
  const previousTarget = useRef<HTMLElement | null | undefined>(target);
  const [initialized, setInitialized] = useState(false);

  const [hidden, setHidden] = useState(
    typeof displayAfterTransitionEnd === 'boolean' ? displayAfterTransitionEnd : false
  );

  const updatePosition = () => {
    if (!target || !parent || !ref.current) {
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    const scaleX = parent.offsetWidth === 0 ? 1 : parentRect.width / parent.offsetWidth;
    const scaleY = parent.offsetHeight === 0 ? 1 : parentRect.height / parent.offsetHeight;

    const targetComputedStyle = window.getComputedStyle(target);
    const parentComputedStyle = window.getComputedStyle(parent);

    const borderTopWidth =
      toInt(targetComputedStyle.borderTopWidth) + toInt(parentComputedStyle.borderTopWidth);
    const borderLeftWidth =
      toInt(targetComputedStyle.borderLeftWidth) + toInt(parentComputedStyle.borderLeftWidth);

    const position = {
      top: (targetRect.top - parentRect.top) / scaleY - borderTopWidth,
      left: (targetRect.left - parentRect.left) / scaleX - borderLeftWidth,
      width: targetRect.width / scaleX,
      height: targetRect.height / scaleY,
    };

    ref.current.style.transform = `translateY(${position.top}px) translateX(${position.left}px)`;
    ref.current.style.width = `${position.width}px`;
    ref.current.style.height = `${position.height}px`;
  };

  const updatePositionWithoutAnimation = () => {
    window.clearTimeout(transitionTimeout.current);
    if (ref.current) {
      ref.current.style.transitionDuration = '0ms';
    }
    updatePosition();
    transitionTimeout.current = window.setTimeout(() => {
      if (ref.current) {
        ref.current.style.transitionDuration = '';
      }
    }, 30);
  };

  const targetResizeObserver = useRef<ResizeObserver>(null);
  const parentResizeObserver = useRef<ResizeObserver>(null);

  useEffect(() => {
    if (initialized && previousTarget.current !== target && onTransitionStart) {
      onTransitionStart();
    }

    previousTarget.current = target;
    updatePosition();

    if (target) {
      targetResizeObserver.current = new ResizeObserver(updatePositionWithoutAnimation);
      targetResizeObserver.current.observe(target);

      if (parent) {
        parentResizeObserver.current = new ResizeObserver(updatePositionWithoutAnimation);
        parentResizeObserver.current.observe(parent);
      }

      return () => {
        targetResizeObserver.current?.disconnect();
        parentResizeObserver.current?.disconnect();
      };
    }

    return undefined;
  }, [parent, target]);

  useEffect(() => {
    if (parent) {
      const handleTransitionEnd = (event: TransitionEvent) => {
        if (isParent(event.target, parent)) {
          updatePositionWithoutAnimation();
          setHidden(false);
        }
      };

      parent.addEventListener('transitionend', handleTransitionEnd);
      return () => {
        parent.removeEventListener('transitionend', handleTransitionEnd);
      };
    }

    return undefined;
  }, [parent]);

  useEffect(() => {
    if (ref.current && onTransitionEnd) {
      const handleIndicatorTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'transform') {
          onTransitionEnd();
        }
      };

      ref.current.addEventListener('transitionend', handleIndicatorTransitionEnd);
      return () => {
        ref.current?.removeEventListener('transitionend', handleIndicatorTransitionEnd);
      };
    }

    return undefined;
  }, [onTransitionEnd]);

  useTimeout(
    () => {
      // Prevents warning about state update without act
      if (getEnv() !== 'test') {
        setInitialized(true);
      }
    },
    20,
    { autoInvoke: true }
  );

  useMutationObserverTarget(
    (mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'dir') {
          updatePositionWithoutAnimation();
        }
      });
    },
    { attributes: true, attributeFilter: ['dir'] },
    () => document.documentElement
  );

  return { initialized, hidden };
}
